import { useQuery } from '@apollo/client'
import { useMemo } from 'react'
import {
  isGlobalAudienceEnabled,
  isMultilangPocEnabled
} from '../../../constants/featureFlags'
import {
  AudienceData,
  GET_AUDIENCES
} from '../../../data/gql/questionnaire/queries/audiences'
import useGetDraftQuestionnaire from '../../../hooks/questionnaire/useGetDraftQuestionnaire'
import { useFieldwork } from '../../../hooks/useFieldwork'
import { useOptionalWaveId } from '../../../hooks/useOptionalWaveId'
import { useProjectId } from '../../../hooks/useProjectId'
import { useSurveyId } from '../../../hooks/useSurveyId'
import { useProjectType } from '../../Project/Project.hooks'
import {
  visibleInAllMarkets,
  visibleInAnyMarket
} from '../../Questionnaire/MarketVisibility/marketVisibility'
import { computeCompletion } from '../lib/completion'
import { extractCustomAudienceMembers } from '../lib/extractAudienceFields'
import { getTranslatableSurveyEntries } from '../lib/extractTranslatableFields'

const MASTER_LANGUAGE = 'en'

// True when the survey carries real translated content in a non-master language
// that is still allocated on the Audience page. Server-side evidence, so unlike
// the browser-local "Needs translation" declaration it is the same for every
// user and every browser — which is what stops a colleague on a second machine
// from launching a half-translated survey.
//
// Deliberately scoped the same way the launch gate and the Translations
// sidebar are, so the checkbox locks exactly when there is something to act on:
//   - `computeCompletion`, not `languagesInUse`: a translation row whose text is
//     empty is not translated content. languagesInUse counts the row's mere
//     existence (right for seeding the sidebar's language list, wrong here) —
//     using it left a survey locked with nothing the Remove-language flow could
//     delete, because that flow also skips languages at 0 translated fields.
//   - allocated languages only: a language dropped on the Audience page can
//     keep its stored translations (see the Translations route), and the launch
//     gate already ignores it, so locking over it would be unclearable too.
export const useHasStoredTranslations = (): boolean => {
  const projectId = useProjectId()
  const surveyId = useSurveyId()
  const waveId = useOptionalWaveId()
  const { fieldwork } = useFieldwork()
  const { draftQuestionnaire } = useGetDraftQuestionnaire()
  const { isMultiMarketV2 } = useProjectType()
  // Same scope as the launch gate — see extractCustomAudienceMembers.
  const globalAudienceOnly = isMultiMarketV2 && isGlobalAudienceEnabled()

  const { data: audienceData } = useQuery<AudienceData>(GET_AUDIENCES, {
    context: { clientName: 'questionnaire' },
    variables: { projectId, surveyId, waveId },
    skip: !isMultilangPocEnabled()
  })

  return useMemo(() => {
    // Flattened, so a translation stored only on an in-section question still
    // counts (see getTranslatableSurveyEntries).
    // So the checkbox can't lock over questions this market doesn't show.
    const isVisible = globalAudienceOnly
      ? visibleInAnyMarket([surveyId])
      : visibleInAllMarkets
    const entries = getTranslatableSurveyEntries(
      draftQuestionnaire?.entries ?? [],
      isVisible
    )
    const audienceGroups = extractCustomAudienceMembers(
      audienceData?.draftAudience.members ?? [],
      { globalOnly: globalAudienceOnly }
    )

    return (fieldwork?.languageAllocations ?? []).some(
      ({ language }) =>
        language !== MASTER_LANGUAGE &&
        computeCompletion(entries, language, audienceGroups, isVisible)
          .translated > 0
    )
  }, [
    fieldwork?.languageAllocations,
    draftQuestionnaire?.entries,
    audienceData?.draftAudience.members,
    globalAudienceOnly,
    surveyId
  ])
}

export default useHasStoredTranslations
